/**
 * Guided strategy-creation page (Phase 1) — /strategies/create
 *
 * Sits between the "New Strategy" buttons and the existing Strategy Builder
 * (/strategies/new). Step 1 picks the strategy type; selecting External
 * Strategy reveals the signal-provider step on the same page. Every active
 * choice navigates immediately — there is no Continue/Next button.
 *
 * Query contract lives in ./guidedCreation.js (maps to the exact builder
 * state/schema enums). Provider logo assets are not available in the repo, so
 * providers use consistent initial-based fallback tiles (no hotlinked or
 * redrawn brand logos).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Radio, Send, Zap } from 'lucide-react';
import { ROUTES } from '../../routes/constants';
import { cn } from '../../lib/utils';
import { buildBuilderUrl } from './guidedCreation';

const STRATEGY_CARDS = [
  {
    key: 'internal',
    label: 'Internal Strategy',
    description: 'Build an automated strategy using the AlgoSys no-code Strategy Builder.',
    support: 'No coding or external platform required.',
    Icon: Zap,
  },
  {
    key: 'external',
    label: 'External Strategy',
    description: 'Connect signals from TradingView, AmiBroker, Python, or another external platform.',
    support: null,
    Icon: Radio,
  },
  {
    key: 'manual',
    label: 'Manual Strategy',
    description: 'Create a Manual Trading strategy and place trades directly from AlgoSys.',
    support: null,
    Icon: Send,
  },
];

const PROVIDER_CARDS = [
  {
    key: 'tradingview',
    label: 'TradingView',
    initials: 'TV',
    description: 'Chart alerts, drawings, indicators, and Pine Script.',
    comingSoon: false,
  },
  {
    key: 'amibroker',
    label: 'AmiBroker',
    initials: 'AB',
    description: 'Send trade signals from AFL strategies.',
    comingSoon: false,
  },
  {
    key: 'python',
    label: 'Python',
    initials: 'Py',
    description: 'Connect a Python application or trading bot.',
    comingSoon: false,
  },
  { key: 'chartink',          label: 'Chartink',         initials: 'CI', description: null, comingSoon: true },
  { key: 'metatrader',        label: 'MetaTrader',       initials: 'MT', description: null, comingSoon: true },
  { key: 'excel',             label: 'Excel',            initials: 'XL', description: null, comingSoon: true },
  { key: 'google-sheets',     label: 'Google Sheets',    initials: 'GS', description: null, comingSoon: true },
  { key: 'chrome-extension',  label: 'Chrome Extension', initials: 'CE', description: null, comingSoon: true },
];

function LogoTile({ initials }) {
  return (
    <div
      aria-hidden="true"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-bg-border bg-bg-surface text-xs font-mono font-semibold text-txt-secondary"
    >
      {initials}
    </div>
  );
}

/**
 * Pure presentational view (exported for focused render tests). All actions
 * are callbacks supplied by the container.
 */
export function StrategyCreateView({ externalSelected, onSelectStrategy, onSelectProvider, onBack }) {
  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div>
        <button
          type="button"
          onClick={onBack}
          className="btn-ghost text-xs flex items-center gap-1.5 mb-4 -ml-3"
        >
          <ArrowLeft size={12} /> Strategies
        </button>
        <h1 className="text-lg md:text-xl font-semibold text-txt-primary">Create a New Strategy</h1>
        <p className="text-xs text-txt-muted font-mono mt-1">How would you like to trade?</p>
      </div>

      {/* ── Step 1: strategy type ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {STRATEGY_CARDS.map(({ key, label, description, support, Icon }) => {
          const selected = key === 'external' && externalSelected;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelectStrategy(key)}
              aria-pressed={key === 'external' ? selected : undefined}
              className={cn(
                'card p-5 text-left flex flex-col gap-3 h-full cursor-pointer transition-colors',
                selected
                  ? 'border-amber/40 bg-amber/5'
                  : 'hover:border-amber/30 hover:bg-bg-hover/40'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-bg-border bg-bg-surface">
                  <Icon size={18} className="text-amber" />
                </div>
                {selected && (
                  <span className="flex items-center gap-1 text-2xs font-mono font-medium text-amber">
                    <Check size={12} /> Selected
                  </span>
                )}
              </div>
              <div>
                <p className="text-sm font-semibold text-txt-primary">{label}</p>
                <p className="text-xs text-txt-secondary mt-1 leading-relaxed">{description}</p>
                {support && <p className="text-2xs font-mono text-txt-muted mt-2">{support}</p>}
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Step 2: external signal provider (revealed after External selection) ── */}
      {externalSelected && (
        <div className="space-y-3 animate-fade-in">
          <h2 className="text-sm font-semibold text-txt-primary">What do you want to connect?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PROVIDER_CARDS.map(({ key, label, initials, description, comingSoon }) => (
              <button
                key={key}
                type="button"
                disabled={comingSoon}
                onClick={comingSoon ? undefined : () => onSelectProvider(key)}
                className={cn(
                  'card p-4 text-left flex items-start gap-3 h-full',
                  comingSoon
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer transition-colors hover:border-amber/30 hover:bg-bg-hover/40'
                )}
              >
                <LogoTile initials={initials} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-xs font-semibold text-txt-primary">{label}</p>
                    {comingSoon && (
                      <span className="inline-flex items-center rounded-full border border-bg-border bg-bg-surface px-1.5 py-0.5 text-2xs font-mono text-txt-muted">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  {description && (
                    <p className="text-2xs text-txt-muted mt-1 leading-relaxed">{description}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function StrategyCreatePage() {
  const navigate = useNavigate();
  const [externalSelected, setExternalSelected] = useState(false);

  function handleSelectStrategy(key) {
    // External reveals the provider step in place; Internal/Manual open the
    // builder immediately with the strategy type fixed.
    if (key === 'external') {
      setExternalSelected(true);
      return;
    }
    navigate(buildBuilderUrl({ type: key }));
  }

  function handleSelectProvider(provider) {
    navigate(buildBuilderUrl({ type: 'external', provider }));
  }

  return (
    <StrategyCreateView
      externalSelected={externalSelected}
      onSelectStrategy={handleSelectStrategy}
      onSelectProvider={handleSelectProvider}
      onBack={() => navigate(ROUTES.STRATEGIES)}
    />
  );
}
